// Floating "+" action button that replaces the bottom dock on /access.
//
// The client did not want the permanent bottom tab bar on the "Choose a
// Projector to View" screen, but its projector actions (Request new access /
// Manage projectors) still have to be reachable. So the dock is suppressed
// there and this FAB takes its place: tapping the plus slides the action chips
// out to the right from underneath the button, and the × slides them back in
// the reverse order. Picking an action leaves the tray open (Manage Projectors
// toggles in place, so the user can undo it from the same chip) — only the ×,
// the backdrop, or Escape closes it.
//
// While open, the whole group (button + chips) glides left by half the tray's
// width so the expanded cluster stays optically centred on screen instead of
// hanging off to the right; closing glides it back to dead centre.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Plus } from 'Utils/lucideIcons';
import './AccessActionsFab.css';

const SPRING = { type: 'spring', stiffness: 460, damping: 32, mass: 0.7 };
// Keep in sync with .afab-tray's `left: calc(100% + Xpx)`.
const TRAY_GAP_PX = 10;

const trayVariants = {
  hidden: { transition: { staggerChildren: 0.06, staggerDirection: -1 } },
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
};

// Slides straight out to the right from under the FAB — no spin on the chips.
const chipVariants = {
  hidden: { opacity: 0, x: -56, scale: 0.9 },
  visible: { opacity: 1, x: 0, scale: 1, transition: SPRING },
};

// actions: [{ key, label, icon: Component, onClick }]
const AccessActionsFab = ({ actions = [], label = 'Projector actions' }) => {
  const [open, setOpen] = useState(false);
  // Tray width, measured so the group can be re-centred while it is open. The
  // tray stays mounted (just faded/tucked away when closed) precisely so this
  // measurement is available before the opening animation runs.
  const trayRef = useRef(null);
  const [trayWidth, setTrayWidth] = useState(0);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  useLayoutEffect(() => {
    const el = trayRef.current;
    if (!el) return undefined;
    const measure = () => setTrayWidth(el.offsetWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [actions.length]);

  // Half of what the tray adds to the right of the button.
  const shift = open ? -((trayWidth + TRAY_GAP_PX) / 2) : 0;

  return (
    <div className="afab-root">
      {open && (
        <button
          type="button"
          aria-label="Close actions"
          className="afab-scrim"
          onClick={close}
        />
      )}

      <motion.div
        className="afab-anchor"
        animate={{ x: shift }}
        transition={SPRING}
      >
        <motion.button
          type="button"
          className={`afab-fab${open ? ' afab-fab--open' : ''}`}
          aria-label={open ? 'Close actions' : label}
          aria-expanded={open}
          aria-haspopup="true"
          onClick={() => setOpen((v) => !v)}
          whileTap={{ scale: 0.92 }}
          transition={SPRING}
        >
          <motion.span
            className="afab-fabIcon"
            animate={{ rotate: open ? 135 : 0 }}
            transition={{ type: 'spring', stiffness: 420, damping: 26 }}
          >
            <Plus size={26} />
          </motion.span>
        </motion.button>

        <motion.div
          ref={trayRef}
          className={`afab-tray${open ? '' : ' afab-tray--closed'}`}
          variants={trayVariants}
          initial="hidden"
          animate={open ? 'visible' : 'hidden'}
          aria-hidden={!open}
        >
          {actions.map(({ key, label: chipLabel, icon: Icon, onClick }) => (
            <motion.button
              key={key}
              type="button"
              className="afab-chip"
              variants={chipVariants}
              onClick={onClick}
              title={chipLabel}
              aria-label={chipLabel}
              tabIndex={open ? 0 : -1}
              whileTap={{ scale: 0.95 }}
            >
              <span className="afab-chipIcon">{Icon ? <Icon size={18} /> : null}</span>
              <span className="afab-chipLabel">{chipLabel}</span>
            </motion.button>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AccessActionsFab;
